import { Component, OnInit } from '@angular/core';
import { MatDatepickerInputEvent } from '@angular/material/datepicker';
import { MatLegacySnackBar as MatSnackBar } from '@angular/material/legacy-snack-bar';

import { FeatureToggleService } from '@app/shared/services/feature-toggle.service';

import { SnackBarConfig, EExcelFileExtension } from '../../../shared';
import { ExportApiService } from '../../../shared/services/api/export-api.service';
import { RfqApiService } from '../../../shared/services/api/rfq/rfq-api.service';

@Component({
  selector: 'lum-reporting-stock-snapshot',
  templateUrl: './reporting-stock-snapshot.component.html',
  styleUrls: ['./reporting-stock-snapshot.component.scss'],
})
export class ReportingStockSnapshotComponent implements OnInit {
  selectedDate :Date | null = new Date();
  selectedTime = '00:00';
  selectedDateEnd :Date | null = new Date();
  selectedTimeEnd = '23:59';
  isExporting = false;
  includeAllZeroStock = false;

  constructor(
    private rfqApiService :RfqApiService,
    private exportApiService :ExportApiService,
    private snackBar :MatSnackBar,
    private featureToggleService :FeatureToggleService,
  ) {}

  ngOnInit() :void {
    const currentDate = new Date();
    if (this.featureToggleService.isActive('red_DV-3389_inventory_reconciliation_report')) {
      this.selectedDate = new Date(currentDate.setHours(0, 0, 0, 0));
      this.selectedDateEnd = new Date(currentDate.setHours(23, 59, 59));
    } else {
      this.selectedDate = new Date(currentDate.setHours(23, 59, 59));
      this.selectedTime = '23:59';
      this.selectedDateEnd = null;
    }
  }

  onDateChange(event :MatDatepickerInputEvent<Date>, field :'start' | 'end') :void {
    const timeString = field === 'start' ? this.selectedTime : this.selectedTimeEnd;

    if (!event.value || !timeString) {
      return;
    }

    const [hours, minutes] = timeString.split(':').map((val) => parseInt(val, 10));

    const updatedDate = new Date(event.value);
    updatedDate.setHours(hours, minutes, 0, 0);

    if (field === 'start') {
      this.selectedDate = updatedDate;
    } else {
      this.selectedDateEnd = updatedDate;
    }
  }

  onTimeChange(event :Event, field :'start' | 'end') :void {
    const target = event.target as HTMLInputElement;
    const selectedTime = target.value;
    const [hours, minutes] = selectedTime.split(':').map(val => parseInt(val, 10));
  
    const dateField = field === 'start' ? 'selectedDate' : 'selectedDateEnd';
    const timeField = field === 'start' ? 'selectedTime' : 'selectedTimeEnd';
  
    const baseDate = this[dateField];
    if (!baseDate) return;
  
    const updatedDate = new Date(baseDate);
    updatedDate.setHours(hours, minutes, 0, 0);
  
    this[timeField] = selectedTime;
    this[dateField] = updatedDate;
  }

  exportToExcel() :void {
    if (!this.selectedDate) {
      this.snackBar.open('Please select a start date for the export', 'Close', {
        duration: SnackBarConfig.DEFAULT.duration,
      });

      return;
    }

    this.isExporting = true;
    const params :{ [key :string] :string | string[] } = {
      dateSnapshot: this.#getSelectedDateISO(),
      exportType: 'xlsx',
    };

    if (this.featureToggleService.isActive('red_DV-3389_inventory_reconciliation_report')) {
      params['dateSnapshotEnd'] = this.selectedDateEnd
        ? this.selectedDateEnd.toISOString()
        : this.selectedDate.toISOString();
    }

    if (this.includeAllZeroStock) {
      params['includeAllData'] = 'true';
    }

    this.rfqApiService.exportStockSnapshot(params).subscribe({
      next: (response) => {
        this.isExporting = false;
        if (response && response instanceof Blob) {
          this.snackBar.open('Stock snapshot export successful', 'Success', {
            duration: SnackBarConfig.DEFAULT.duration,
          });
          const fileName = `Stock-Snapshot-${this.#formatDateForFileName(this.selectedDate)}`;
          this.exportApiService.saveBlobAsFile(
            response,
            fileName,
            EExcelFileExtension.XLSX,
          );
        } else {
          this.snackBar.open('No data found for export', 'Error', {
            duration: SnackBarConfig.DEFAULT.duration,
          });
        }
      },
      error: (error) => {
        this.isExporting = false;
        this.snackBar.open(
          error?.error?.message || 'Error exporting data',
          'Error',
          {
            duration: SnackBarConfig.DEFAULT.duration,
          },
        );
      },
    });
  }

  #getSelectedDateISO() :string {
    if (!this.selectedDate) {
      return new Date().toISOString();
    }

    return new Date(this.selectedDate).toISOString();
  }

  #formatDateForFileName(date :Date | null) :string {
    if (!date) return '';

    return date.toISOString().split('T')[0];
  }
}
